/**
 * useSupport Hook
 *
 * React hook for managing support chat conversations with AI-first support.
 * Handles conversations, messages, and real-time updates.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getCategories,
  getMyConversations,
  createConversation,
  getConversation,
  sendMessage,
  closeConversation,
  rateConversation,
  getUnreadCount,
  askAI,
  type SupportCategory,
  type SupportConversation,
  type SupportMessage,
} from '@/services/support';
import { notificationSSE } from '@/services/notifications';
import { toastManager } from './useNotifications';

// ============================================================================
// Types
// ============================================================================

export interface UseSupportOptions {
  /** Auto-fetch conversations on mount (default: true) */
  autoFetch?: boolean;
  /** Subscribe to real-time updates (default: true) */
  subscribeToUpdates?: boolean;
  /** Show toasts for new messages (default: true) */
  showToasts?: boolean;
}

export interface UseSupportReturn {
  /** Support categories */
  categories: SupportCategory[];
  /** User's conversations */
  conversations: SupportConversation[];
  /** Currently active conversation */
  activeConversation: SupportConversation | null;
  /** Messages for active conversation */
  messages: SupportMessage[];
  /** Unread message count */
  unreadCount: number;
  /** Loading state */
  isLoading: boolean;
  /** Sending message state */
  isSending: boolean;
  /** Error message */
  error: string | null;
  /** Start a new conversation */
  startConversation: (
    categoryId: string,
    subject: string,
    message: string,
    priority?: 'low' | 'normal' | 'high' | 'urgent'
  ) => Promise<SupportConversation | null>;
  /** Send a message in the active conversation */
  sendChatMessage: (content: string) => Promise<void>;
  /** Select a conversation to view */
  selectConversation: (id: string) => Promise<void>;
  /** Close active conversation */
  closeChatConversation: () => Promise<void>;
  /** Rate a conversation */
  rateChat: (rating: number, feedback?: string) => Promise<void>;
  /** Clear active conversation */
  clearActiveConversation: () => void;
  /** Refresh conversations list */
  refreshConversations: () => Promise<void>;
  /** Ask AI directly without creating conversation */
  askAIQuestion: (question: string) => Promise<string | null>;
  /** Check if chat widget should be shown */
  isWidgetOpen: boolean;
  /** Toggle chat widget */
  setWidgetOpen: (open: boolean) => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useSupport(options: UseSupportOptions = {}): UseSupportReturn {
  const {
    autoFetch = true,
    subscribeToUpdates = true,
    showToasts = true,
  } = options;

  const [categories, setCategories] = useState<SupportCategory[]>([]);
  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<SupportConversation | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isWidgetOpen, setWidgetOpen] = useState(false);

  const isMounted = useRef(true);
  const activeConversationId = useRef<string | null>(null);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await getCategories();
      console.log('[useSupport] Categories response:', response);
      if (response.success && response.data) {
        setCategories(response.data);
      } else {
        console.error('[useSupport] Failed to fetch categories:', response.error);
        // Set default categories as fallback
        setCategories([
          { id: 'technical', name: 'Technical Issue', description: 'System errors, bugs, or technical problems', icon: 'bug', priority: 1, is_active: 1 },
          { id: 'account', name: 'Account & Access', description: 'Login issues, permissions, user management', icon: 'user', priority: 2, is_active: 1 },
          { id: 'training', name: 'Training & Guidance', description: 'How-to questions, feature explanations', icon: 'book', priority: 3, is_active: 1 },
          { id: 'compliance', name: 'Compliance & Policy', description: 'Retention policies, legal holds, audits', icon: 'shield', priority: 4, is_active: 1 },
          { id: 'feature', name: 'Feature Request', description: 'Suggestions for improvements', icon: 'lightbulb', priority: 5, is_active: 1 },
          { id: 'other', name: 'Other', description: 'General inquiries', icon: 'help-circle', priority: 6, is_active: 1 },
        ]);
      }
    } catch (err) {
      console.error('[useSupport] Failed to fetch categories:', err);
      // Set default categories as fallback
      setCategories([
        { id: 'technical', name: 'Technical Issue', description: 'System errors, bugs, or technical problems', icon: 'bug', priority: 1, is_active: 1 },
        { id: 'account', name: 'Account & Access', description: 'Login issues, permissions, user management', icon: 'user', priority: 2, is_active: 1 },
        { id: 'training', name: 'Training & Guidance', description: 'How-to questions, feature explanations', icon: 'book', priority: 3, is_active: 1 },
        { id: 'compliance', name: 'Compliance & Policy', description: 'Retention policies, legal holds, audits', icon: 'shield', priority: 4, is_active: 1 },
        { id: 'feature', name: 'Feature Request', description: 'Suggestions for improvements', icon: 'lightbulb', priority: 5, is_active: 1 },
        { id: 'other', name: 'Other', description: 'General inquiries', icon: 'help-circle', priority: 6, is_active: 1 },
      ]);
    }
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!isMounted.current) return;

    setIsLoading(true);
    setError(null);

    try {
      const [conversationsRes, unreadRes] = await Promise.all([
        getMyConversations(),
        getUnreadCount(),
      ]);

      if (!isMounted.current) return;

      if (conversationsRes.success && conversationsRes.data) {
        setConversations(conversationsRes.data);
      }

      if (unreadRes.success && unreadRes.data) {
        setUnreadCount(unreadRes.data.unreadCount);
      }
    } catch (err) {
      if (!isMounted.current) return;
      setError(err instanceof Error ? err.message : 'Failed to fetch conversations');
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // Start a new conversation
  const startConversation = useCallback(async (
    categoryId: string,
    subject: string,
    message: string,
    priority?: 'low' | 'normal' | 'high' | 'urgent'
  ): Promise<SupportConversation | null> => {
    setIsSending(true);
    setError(null);

    try {
      const response = await createConversation({ categoryId, subject, message, priority });

      if (response.success && response.data) {
        const { conversation, aiResponse, aiCanHelp } = response.data;

        // Build initial messages
        const initialMessages: SupportMessage[] = [{
          id: 'initial-' + Date.now(),
          conversation_id: conversation.id,
          sender_id: conversation.initiated_by,
          sender_name: conversation.initiated_by_name,
          sender_type: 'user',
          content: message,
          message_type: 'text',
          attachment_url: null,
          attachment_name: null,
          attachment_type: null,
          is_read: 1,
          read_at: null,
          created_at: conversation.created_at,
        }];

        if (aiResponse) {
          initialMessages.push(aiResponse);
        }

        setActiveConversation(conversation);
        setMessages(initialMessages);
        activeConversationId.current = conversation.id;

        // Refresh conversations list
        await fetchConversations();

        if (aiCanHelp && showToasts) {
          toastManager.info('AI Assistant', 'Our AI assistant is ready to help!');
        }

        return conversation;
      }

      setError(response.error?.message || 'Failed to create conversation');
      return null;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create conversation');
      return null;
    } finally {
      setIsSending(false);
    }
  }, [fetchConversations, showToasts]);

  // Select a conversation
  const selectConversation = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getConversation(id);

      if (response.success && response.data) {
        setActiveConversation(response.data.conversation);
        setMessages(response.data.messages);
        activeConversationId.current = id;

        // Update unread count in conversations list
        setConversations((prev) =>
          prev.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c))
        );

        // Refresh unread count
        const unreadRes = await getUnreadCount();
        if (unreadRes.success && unreadRes.data) {
          setUnreadCount(unreadRes.data.unreadCount);
        }
      } else {
        setError(response.error?.message || 'Failed to load conversation');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversation');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Send a message
  const sendChatMessage = useCallback(async (content: string) => {
    if (!activeConversationId.current) {
      setError('No active conversation');
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const response = await sendMessage(activeConversationId.current, { content });

      if (response.success && response.data) {
        const { message, aiResponse } = response.data;

        // Add user message
        setMessages((prev) => [...prev, message]);

        // Add AI response if present
        if (aiResponse) {
          setMessages((prev) => [...prev, aiResponse.message]);
        }

        // Update conversation's last message time
        setConversations((prev) =>
          prev.map((c) =>
            c.id === activeConversationId.current
              ? { ...c, last_message_at: new Date().toISOString() }
              : c
          )
        );
      } else {
        setError(response.error?.message || 'Failed to send message');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  }, []);

  // Close conversation
  const closeChatConversation = useCallback(async () => {
    if (!activeConversationId.current) return;

    try {
      const response = await closeConversation(activeConversationId.current);

      if (response.success) {
        setActiveConversation((prev) =>
          prev ? { ...prev, status: 'closed' } : null
        );
        setConversations((prev) =>
          prev.map((c) =>
            c.id === activeConversationId.current
              ? { ...c, status: 'closed' }
              : c
          )
        );

        if (showToasts) {
          toastManager.success('Conversation Closed', 'Your support conversation has been closed.');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close conversation');
    }
  }, [showToasts]);

  // Rate conversation
  const rateChat = useCallback(async (rating: number, feedback?: string) => {
    if (!activeConversationId.current) return;

    try {
      const response = await rateConversation(activeConversationId.current, rating, feedback);

      if (response.success) {
        setActiveConversation((prev) =>
          prev ? { ...prev, rating, rating_feedback: feedback || null } : null
        );

        if (showToasts) {
          toastManager.success('Thank You', 'Your feedback has been recorded.');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit rating');
    }
  }, [showToasts]);

  // Clear active conversation
  const clearActiveConversation = useCallback(() => {
    setActiveConversation(null);
    setMessages([]);
    activeConversationId.current = null;
  }, []);

  // Refresh conversations and categories
  const refreshConversations = useCallback(async () => {
    await Promise.all([
      fetchConversations(),
      fetchCategories(),
    ]);
  }, [fetchConversations, fetchCategories]);

  // Ask AI directly
  const askAIQuestion = useCallback(async (question: string): Promise<string | null> => {
    try {
      const response = await askAI(question);
      if (response.success && response.data) {
        return response.data.response;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  // Handle new support message from SSE
  const handleSupportMessage = useCallback((data: unknown) => {
    const event = data as {
      conversationId: string;
      messageId: string;
      content: string;
      senderName: string;
      senderType: 'user' | 'admin' | 'system';
      createdAt: string;
    };

    // If this message is for the active conversation, add it
    if (event.conversationId === activeConversationId.current) {
      const newMessage: SupportMessage = {
        id: event.messageId,
        conversation_id: event.conversationId,
        sender_id: '',
        sender_name: event.senderName,
        sender_type: event.senderType,
        content: event.content,
        message_type: 'text',
        attachment_url: null,
        attachment_name: null,
        attachment_type: null,
        is_read: 1,
        read_at: null,
        created_at: event.createdAt,
      };

      setMessages((prev) => {
        // Avoid duplicates
        if (prev.some((m) => m.id === event.messageId)) {
          return prev;
        }
        return [...prev, newMessage];
      });
    } else {
      // Update unread count for other conversations
      setConversations((prev) =>
        prev.map((c) =>
          c.id === event.conversationId
            ? { ...c, unread_count: (c.unread_count || 0) + 1 }
            : c
        )
      );
      setUnreadCount((prev) => prev + 1);
    }

    // Show toast if chat is minimized or different conversation
    if (showToasts && (!isWidgetOpen || event.conversationId !== activeConversationId.current)) {
      toastManager.info('New Support Message', `${event.senderName}: ${event.content.substring(0, 50)}...`);
    }
  }, [showToasts, isWidgetOpen]);

  // Handle conversation assigned
  const handleSupportAssigned = useCallback((data: unknown) => {
    const event = data as { conversationId: string; subject: string };

    setConversations((prev) =>
      prev.map((c) =>
        c.id === event.conversationId
          ? { ...c, status: 'assigned' as const }
          : c
      )
    );

    if (activeConversationId.current === event.conversationId) {
      setActiveConversation((prev) =>
        prev ? { ...prev, status: 'assigned' } : null
      );
    }

    if (showToasts) {
      toastManager.info('Support Assigned', 'A support agent is now handling your request.');
    }
  }, [showToasts]);

  // Handle conversation resolved
  const handleSupportResolved = useCallback((data: unknown) => {
    const event = data as { conversationId: string; subject: string };

    setConversations((prev) =>
      prev.map((c) =>
        c.id === event.conversationId
          ? { ...c, status: 'resolved' as const }
          : c
      )
    );

    if (activeConversationId.current === event.conversationId) {
      setActiveConversation((prev) =>
        prev ? { ...prev, status: 'resolved' } : null
      );
    }

    if (showToasts) {
      toastManager.success('Support Resolved', 'Your support request has been marked as resolved.');
    }
  }, [showToasts]);

  // Setup on mount
  useEffect(() => {
    isMounted.current = true;

    if (autoFetch) {
      fetchCategories();
      fetchConversations();
    }

    // Subscribe to SSE events
    if (subscribeToUpdates) {
      notificationSSE.subscribe('support:new_message', handleSupportMessage);
      notificationSSE.subscribe('support:assigned', handleSupportAssigned);
      notificationSSE.subscribe('support:resolved', handleSupportResolved);
    }

    return () => {
      isMounted.current = false;

      if (subscribeToUpdates) {
        notificationSSE.unsubscribe('support:new_message', handleSupportMessage);
        notificationSSE.unsubscribe('support:assigned', handleSupportAssigned);
        notificationSSE.unsubscribe('support:resolved', handleSupportResolved);
      }
    };
  }, [
    autoFetch,
    subscribeToUpdates,
    fetchCategories,
    fetchConversations,
    handleSupportMessage,
    handleSupportAssigned,
    handleSupportResolved,
  ]);

  return {
    categories,
    conversations,
    activeConversation,
    messages,
    unreadCount,
    isLoading,
    isSending,
    error,
    startConversation,
    sendChatMessage,
    selectConversation,
    closeChatConversation,
    rateChat,
    clearActiveConversation,
    refreshConversations,
    askAIQuestion,
    isWidgetOpen,
    setWidgetOpen,
  };
}
